import { useState, useEffect } from 'react';
import { ClubRecommendation, UserProfile } from './interfaces';
import { fetchRecommendations as fetchRecsApi } from './useUserProfile';
import { AFFILIATE_TRACKING_URLS } from './constants';

export const useClubFitting = () => {
    // Load data from localStorage or use defaults
    const loadFromStorage = () => {
        try {
            const savedData = localStorage.getItem('club-fitting-results');
            if (savedData) {
                const parsed = JSON.parse(savedData);
                const sanitizedRecommendations = (parsed.recommendations || []).filter((recommendation: ClubRecommendation) => {
                    const normalizedModel = recommendation.model?.trim().toLowerCase();
                    const normalizedLinks = (recommendation.links || []).flatMap((link) => [link.url]).join(' ').toLowerCase();
                    const isDeprecatedRamRecommendation = normalizedModel === 'ram golf accubar golf clubs set, mens right hand' ||
                        normalizedLinks.includes('ram.sjv.io') ||
                        normalizedLinks.includes('ramgolf.com') ||
                        recommendation.image?.includes('golf-1486354.jpg');

                    return !isDeprecatedRamRecommendation;
                });

                return {
                    recommendations: sanitizedRecommendations,
                    hasFetched: parsed.hasFetched || false,
                    overview: parsed.overview || "After updating your player profile, click the button above to get a personalized analysis based on your player profile.",
                    compare: parsed.compare || null
                };
            }
        } catch (error) {
            console.error('Error loading club fitting results from localStorage:', error);
        }
        // Temporarily disabled legacy RAM fallback recommendation:
        // return {
        //     recommendations: [
        //         {
        //           model: "Ram Golf Accubar Golf Clubs Set, Mens Right Hand",
        //           price: "$279.99",
        //           shaft: "Graphite/Steel Combo",
        //           reason: "A great all-in-one set for beginners and intermediates, offering excellent value and forgiveness.",
        //           benefits: [
        //             "Complete set for most golfers",
        //             "Forgiving club heads",
        //             "Modern minimalist design",
        //             "Great value for the price"
        //           ],
        //           links: [
        //             { retailer: "Other", url: "https://ram.sjv.io/c/6401536/1642699/18931?u=https%3A%2F%2Fwww.ramgolf.com%2Fram-golf-accubar-golf-clubs-set-mens-right-hand%2F", price: "$279.99" }
        //           ],
        //           image: "/golf-1486354.jpg"
        //         }
        //     ],
        //     hasFetched: false,
        //     overview: "After updating your player profile, click the button above to get a personalized analysis based on your player profile.",
        //     compare: null
        // };
        return {
            recommendations: [],
            hasFetched: false,
            overview: "NeutralFit is a personalized golf club fitting and recommendation app built to help golfers match equipment to their actual swing tendencies, goals, and course conditions. By answering questions about your common miss, preferred ball flight, launch, turf interaction, and budget, golfers can get more relevant recommendations for drivers, fairway woods, utilities, irons, wedges, putters, and full sets.\n\nIn short, NeutralFit turns general club shopping into a more informed fitting experience with clearer buying guidance and more confident choices for how you actually play.",
            compare: null
        };
    };

    const initialData = loadFromStorage();
    const [recommendations, setRecommendations] = useState<ClubRecommendation[]>(initialData.recommendations);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [hasFetched, setHasFetched] = useState(initialData.hasFetched);
    const [overview, setOverview] = useState<string>(initialData.overview);
    const [compare, setCompare] = useState<string | null>(initialData.compare);

    // Save to localStorage whenever results change
    useEffect(() => {
        try {
            const dataToSave = {
                recommendations,
                hasFetched,
                overview,
                compare
            };
            localStorage.setItem('club-fitting-results', JSON.stringify(dataToSave));
        } catch (error) {
            console.error('Error saving club fitting results to localStorage:', error);
        }
    }, [recommendations, hasFetched, overview, compare]);

    const fetchRecommendations = async (userProfile: UserProfile, isLoggedIn: boolean, club?: string, brands?: string, budget?: string, condition?: 'new' | 'used', lookupQuery?: string) => {
        if (!userProfile || Object.keys(userProfile).length === 0) {
            setError("User profile is not available. Please complete your profile first.");
            return;
        }
        setLoading(true);
        setError(null);
        setHasFetched(true);
        setCompare(null); // Reset comparison text

        try {
            const token = localStorage.getItem('token');
            if (isLoggedIn && !token) {
                setError('Authentication token not found. Please log in again.');
                setLoading(false);
                return;
            }

            const data = await fetchRecsApi(token, userProfile, club, brands, budget, condition, lookupQuery);

            if (data.overview) {
                setOverview(data.overview);
            }

            if (data.compare) {
                setCompare(data.compare);
            }

            if (data.items) {
                const loadedRecommendations = Object.values(data.items).map((rec: any) => {
                    // Create base links from API response
                    let processedLinks = (rec.links || []).map((url: string) => {
                        let retailer: 'eBay' | 'PGA Tour Superstore' | 'Global Golf' | '2nd Swing';
                        let affiliateUrl = url; // Default to original URL
                        
                        if (url.includes('pgatoursuperstore.com')) {
                            retailer = 'PGA Tour Superstore';
                            // Transform to your specific PGA Tour Superstore tracking link
                            affiliateUrl = `${AFFILIATE_TRACKING_URLS.PGA_TOUR_SUPERSTORE}?u=${encodeURIComponent(url)}`;
                        } else if (url.includes('globalgolf.com')) {
                            retailer = 'Global Golf';
                            // Transform to your specific Global Golf tracking link  
                            affiliateUrl = `${AFFILIATE_TRACKING_URLS.GLOBAL_GOLF}?u=${encodeURIComponent(url)}`;
                        } else if (url.includes('2ndswing.com')) {
                            retailer = '2nd Swing';
                            // Second Swing affiliate links use Awin convert-a-link
                        } else if (url.includes('ebay.com')) {
                            retailer = 'eBay';
                            // eBay affiliate links use their own partner network
                        } else {
                            retailer = '2nd Swing'; // Default fallback
                        }
                        
                        return {
                            retailer,
                            url: affiliateUrl
                        };
                    });

                    // Add eBay search link for lookup queries
                    if (lookupQuery && !processedLinks.some((link: {retailer: string, url: string}) => link.retailer === 'eBay')) {
                        const ebaySearchUrl = `${AFFILIATE_TRACKING_URLS.EBAY}?_nkw=${encodeURIComponent(lookupQuery)}&_sacat=0&LH_ItemCondition=${condition === 'new' ? '1000' : '3000'}&rt=nc`;
                        processedLinks.push({
                            retailer: 'eBay',
                            url: ebaySearchUrl
                        });
                    }

                    return {
                        ...rec,
                        image: rec.image_url || rec.image,
                        links: processedLinks
                    };
                });
                
                setRecommendations(loadedRecommendations);
            } else if (data.item) {
                // Create base links from API response
                let processedLinks = (data.item.links || []).map((url: string) => {
                    let retailer: 'eBay' | 'PGA Tour Superstore' | 'Global Golf' | '2nd Swing';
                    let affiliateUrl = url; // Default to original URL
                    
                    if (url.includes('pgatoursuperstore.com')) {
                        retailer = 'PGA Tour Superstore';
                        // Transform to your specific PGA Tour Superstore tracking link
                        affiliateUrl = `${AFFILIATE_TRACKING_URLS.PGA_TOUR_SUPERSTORE}?u=${encodeURIComponent(url)}`;
                    } else if (url.includes('globalgolf.com')) {
                        retailer = 'Global Golf';
                        // Transform to your specific Global Golf tracking link  
                        affiliateUrl = `${AFFILIATE_TRACKING_URLS.GLOBAL_GOLF}?u=${encodeURIComponent(url)}`;
                    } else if (url.includes('2ndswing.com')) {
                        retailer = '2nd Swing';
                        // Second Swing affiliate links use Awin convert-a-link
                    } else if (url.includes('ebay.com')) {
                        retailer = 'eBay';
                        // eBay affiliate links use their own partner network
                    } else {
                        retailer = '2nd Swing'; // Default fallback
                    }
                    
                    return {
                        retailer,
                        url: affiliateUrl
                    };
                });

                // Add eBay search link for lookup queries
                if (lookupQuery && !processedLinks.some((link: {retailer: string, url: string}) => link.retailer === 'eBay')) {
                    const ebaySearchUrl = `${AFFILIATE_TRACKING_URLS.EBAY}?_nkw=${encodeURIComponent(lookupQuery)}&_sacat=0&LH_ItemCondition=${condition === 'new' ? '1000' : '3000'}&rt=nc`;
                    processedLinks.push({
                        retailer: 'eBay',
                        url: ebaySearchUrl
                    });
                }

                const singleRecommendation = {
                    ...data.item,
                    image: data.item.image_url || data.item.image,
                    links: processedLinks
                };
                
                setRecommendations([singleRecommendation]);
            } else {
                setRecommendations([]);
            }

            setHasFetched(true);
        } catch (error) {
            setError(`Failed to load recommendations: ${error instanceof Error ? error.message : 'Unknown error'}`);
        } finally {
            setLoading(false);
        }
    };

    const clearResults = () => {
        // Temporarily disabled legacy RAM fallback recommendation:
        // const defaultData = {
        //     recommendations: [
        //         {
        //           model: "Ram Golf Accubar Golf Clubs Set, Mens Right Hand",
        //           price: "$279.99",
        //           shaft: "Graphite/Steel Combo",
        //           reason: "A great all-in-one set for beginners and intermediates, offering excellent value and forgiveness.",
        //           benefits: [
        //             "Complete set for most golfers",
        //             "Forgiving club heads",
        //             "Modern minimalist design",
        //             "Great value for the price"
        //           ],
        //           links: [
        //             { retailer: "Other" as const, url: "https://ram.sjv.io/c/6401536/1642699/18931?u=https%3A%2F%2Fwww.ramgolf.com%2Fram-golf-accubar-golf-clubs-set-mens-right-hand%2F", price: "$279.99" }
        //           ],
        //           image: "/golf-1486354.jpg"
        //         }
        //     ],
        //     hasFetched: false,
        //     overview: "After updating your player profile, click the button above to get a personalized analysis based on your player profile.",
        //     compare: null
        // };
        const defaultData = {
            recommendations: [],
            hasFetched: false,
            overview: "NeutralFit is a personalized golf club fitting and recommendation app built to help golfers match equipment to their actual swing tendencies, goals, and course conditions. By answering questions about your common miss, preferred ball flight, launch, turf interaction, and budget, golfers can get more relevant recommendations for drivers, fairway woods, utilities, irons, wedges, putters, and full sets.\n\nIn short, NeutralFit turns general club shopping into a more informed fitting experience with clearer buying guidance and more confident choices for how you actually play.",
            compare: null
        };
        
        setRecommendations(defaultData.recommendations);
        setHasFetched(defaultData.hasFetched);
        setOverview(defaultData.overview);
        setCompare(defaultData.compare);
        setError(null);
        localStorage.setItem('club-fitting-results', JSON.stringify(defaultData));
    };

    return {
        recommendations,
        loading,
        error,
        hasFetched,
        overview,
        compare,
        fetchRecommendations,
        clearResults,
        setError
    };
};
